<div class="domain-topics-container">
  <header class="topics-header">
    <div class="topics-header-top">
      <div>
        <h1>📊 Domain Topic Authority</h1>
        <p *ngIf="domain">Visualize topics your domain owns and understand why — <strong>{{ domain }}</strong></p>
        <p *ngIf="!domain">Select an active domain from the header to load topics.</p>
      </div>
      <!-- Share Button -->
      <button
        *ngIf="topics.length > 0 && !shareUrl"
        class="share-btn"
        (click)="createShareLink()"
        [disabled]="shareLoading"
        title="Create a public share link for this domain overview"
      >
        {{ shareLoading ? '⏳' : '🔗' }} Share
      </button>
    </div>

    <!-- Inline Share URL (appears after creating a share link) -->
    <div *ngIf="shareUrl" class="share-inline">
      <input class="share-url-input" type="text" [value]="shareUrl" readonly #shareInput (click)="shareInput.select()" />
      <button class="share-copy-btn" (click)="copyShareUrl()">
        {{ shareCopied ? '✓ Copied' : '📋 Copy' }}
      </button>
      <button class="share-dismiss-btn" (click)="dismissShare()" title="Dismiss">×</button>
    </div>
    <div *ngIf="shareError" class="share-error">{{ shareError }}</div>

    <div class="view-toggle" *ngIf="!featureFlags.isUserView()">
      <button 
        [class.active]="viewMode === 'list'" 
        (click)="switchView('list')"
        class="toggle-btn"
      >
        List View
      </button>
      <button 
        [class.active]="viewMode === 'graph'" 
        (click)="switchView('graph')"
        class="toggle-btn"
      >
        Graph View
      </button>
      <button
        [class.active]="viewMode === 'tree'"
        (click)="switchView('tree')"
        class="toggle-btn"
      >
        Tree View
      </button>
      <button
        [class.active]="viewMode === 'old-user-list'"
        (click)="switchView('old-user-list')"
        class="toggle-btn"
      >
        Old User List View
      </button>
    </div>
  </header>

  <div *ngIf="error" class="error-message">{{ error }}</div>

  <!-- Graph View -->
  <section *ngIf="viewMode === 'graph'" class="graph-section">
    <div class="graph-controls">
      <label>
        Source:
        <select [(ngModel)]="graphDataSource" class="similarity-input">
          <option value="overview">Overview (Top-N, persisted edges)</option>
          <option value="legacy">Legacy (runtime similarity)</option>
        </select>
      </label>
      <label>
        Min Similarity:
        <input 
          type="number" 
          [(ngModel)]="minSimilarity" 
          min="0" 
          max="1" 
          step="0.05"
          (change)="loadGraph()"
          class="similarity-input"
        />
      </label>
      <label>
        Top N:
        <input
          type="number"
          [(ngModel)]="topN"
          min="5"
          max="200"
          step="5"
          class="similarity-input"
        />
      </label>
      <label>
        Min Confidence:
        <input
          type="number"
          [(ngModel)]="minGraphConfidence"
          min="0"
          max="1"
          step="0.05"
          class="similarity-input"
        />
      </label>
      <label>
        Max Edges/Node:
        <input
          type="number"
          [(ngModel)]="maxEdgesPerNode"
          min="1"
          max="50"
          step="1"
          class="similarity-input"
        />
      </label>
      <label>
        Search:
        <input
          type="text"
          [(ngModel)]="graphSearch"
          placeholder="topic slug or label"
          class="similarity-input"
        />
      </label>
      <button (click)="loadGraph()" [disabled]="loadingGraph" class="load-btn">
        {{ loadingGraph ? 'Loading...' : 'Refresh Graph' }}
      </button>
    </div>

    <div *ngIf="loadingGraph" class="loading-message">Loading graph...</div>
    
    <div *ngIf="graphError" class="error-message">{{ graphError }}</div>
    
    <div *ngIf="!loadingGraph && !graphError && graphNodes.length > 0" class="graph-container">
      <div class="graph-stats">
        <span>{{ graphNodes.length }} topics</span>
        <span>{{ graphEdges.length }} relationships</span>
      </div>

      <div class="graph-nodes">
        <h3>Topics (Nodes)</h3>
        <div class="nodes-list">
          <div *ngFor="let node of graphNodes" 
               class="graph-node" 
               [style.border-color]="getNodeColor(node)"
               (click)="openTopicTree(node)"
               [class.node-clickable]="true">
            <div class="node-header">
              <strong>{{ node.label }}</strong>
              <span class="node-id">{{ node.id }}</span>
            </div>
            <div class="node-details">
              <span>Authority: {{ node.authorityScore.toFixed(1) }}</span>
              <span>Pages: {{ node.pageCount }}</span>
              <span *ngIf="node.grandparentTopic" class="grandparent-badge">
                Grandparent: {{ node.grandparentTopic }}
              </span>
            </div>
            <div *ngIf="node.pathToGrandparent && node.pathToGrandparent.length > 0" class="node-transitive-path">
              <div class="path-label">Path to grandparent (via):</div>
              <div class="path-topics">
                <span class="path-topic">{{ node.id }}</span>
                <span *ngFor="let topic of node.pathToGrandparent" class="path-segment">
                  <span class="path-arrow"> → </span>
                  <span class="path-topic">{{ topic }}</span>
                </span>
                <span class="path-arrow"> → </span>
                <span class="path-topic path-grandparent">{{ node.grandparentTopic }}</span>
              </div>
              <div class="path-explanation">
                These intermediate topics connect {{ node.id }} to its grandparent {{ node.grandparentTopic }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="graph-edges">
        <h3>Relationships (Edges)</h3>
        <div class="edges-list">
          <div *ngFor="let edge of graphEdges" class="graph-edge" [style.border-left-color]="getEdgeColor(edge)">
            <div class="edge-header">
              <span class="edge-topics">
                <strong>{{ edge.source }}</strong>
                <span class="edge-arrow">↔</span>
                <strong>{{ edge.target }}</strong>
              </span>
              <span *ngIf="getGrandparentForEdge(edge)" class="grandparent-indicator" title="Grandparent topic (most frequent)">
                Grandparent: <strong>{{ getGrandparentForEdge(edge) }}</strong>
              </span>
            </div>
            <div class="edge-details">
              <span class="similarity-badge" [style.background-color]="getEdgeColor(edge)">
                Similarity: {{ (edge.similarity * 100).toFixed(1) }}%
              </span>
              <span class="edge-type">{{ edge.type }}</span>
            </div>
            <div *ngIf="edge.transitivePath && edge.transitivePath.length > 0" class="transitive-path">
              <div class="path-label">Transitive path (via):</div>
              <div class="path-topics">
                <span *ngFor="let topic of edge.transitivePath; let last = last" class="path-topic">
                  {{ topic }}<span *ngIf="!last" class="path-arrow"> → </span>
                </span>
              </div>
              <div class="path-explanation">
                These topics connect {{ edge.source }} and {{ edge.target }} (direct similarity: {{ (edge.similarity * 100).toFixed(1) }}%)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="!loadingGraph && !graphError && graphNodes.length === 0" class="no-data">
      No graph data. Click "Refresh Graph" to load.
    </div>
  </section>

  <!-- Tree View -->
  <section *ngIf="viewMode === 'tree'" class="tree-view-section">
    <div class="tree-view-header">
      <h2>Tree View (Top 3 clusters)</h2>
      <div class="tree-view-actions">
        <div class="tree-tag-mode-toggle">
          <span class="tree-tag-mode-label">Tag Labels</span>
          <button
            type="button"
            class="tree-tag-mode-btn"
            [class.active]="treeTagLabelMode === 'variant'"
            (click)="setTreeTagLabelMode('variant')"
          >
            Variant
          </button>
          <button
            type="button"
            class="tree-tag-mode-btn"
            [class.active]="treeTagLabelMode === 'canonical'"
            (click)="setTreeTagLabelMode('canonical')"
          >
            Canonical
          </button>
        </div>
        <button (click)="loadTreeView()" [disabled]="loadingTreeView" class="load-btn">
          {{ loadingTreeView ? 'Loading...' : 'Refresh Tree' }}
        </button>
      </div>
    </div>

    <div *ngIf="loadingTreeView" class="loading-message">Loading tree view...</div>
    <div *ngIf="treeViewError" class="error-message">{{ treeViewError }}</div>

    <div *ngIf="!loadingTreeView && treeEntries.length > 0" class="tree-topic-list">
      <article *ngFor="let entry of treeEntries" class="tree-topic-card">
        <header class="tree-topic-card-header">
          <div class="tree-topic-title">
            <h3>{{ entry.topic.label }}</h3>
            <span class="tree-topic-slug">{{ entry.topic.parentTopicSlug }}</span>
            <div
              *ngIf="entry.topic.topicTier"
              [class]="getTierBadgeClass(entry.topic.topicTier)"
              [title]="getTierDescription(entry.topic.topicTier)"
            >
              {{ getTierLabel(entry.topic.topicTier) }}
            </div>
          </div>
        </header>

        <div class="tree-topic-metrics">
          <span>Authority {{ entry.topic.authorityScore.toFixed(1) }}</span>
          <span>Breadth {{ (entry.topic.breadthScore * 100).toFixed(1) }}%</span>
          <span>Depth {{ (entry.topic.depthScore * 100).toFixed(1) }}%</span>
          <span>Coverage {{ (entry.topic.coverageScore * 100).toFixed(1) }}%</span>
          <span>Strength {{ (entry.topic.strengthScore * 100).toFixed(1) }}%</span>
          <span>Focus {{ (entry.topic.focusScore * 100).toFixed(1) }}%</span>
          <span *ngIf="entry.topic.linkScore !== undefined">Links {{ (entry.topic.linkScore! * 100).toFixed(1) }}%</span>
        </div>
        <div class="tree-cluster-summary">
          Cluster coverage: <strong>{{ entry.topic.pageCount }}</strong> pages mentioning this cluster.
        </div>

        <div *ngIf="entry.loading" class="loading-message">Loading subtopics...</div>
        <div *ngIf="entry.error" class="error-message">{{ entry.error }}</div>

        <div *ngIf="entry.data && !entry.loading" class="tree-topic-sections">
          <section class="tree-subtopic-section">
            <h4>Primary Cluster Topic</h4>
            <p class="tree-section-note">Canonical topic for this cluster. It typically has the broadest page coverage.</p>
            <div *ngIf="getPrimaryDirectSubtopic(entry) as primarySubtopic; else noPrimaryClusterTopic" class="tree-subtopic-card">
              <button
                type="button"
                class="tree-subtopic-toggle"
                (click)="toggleSubtopic(getSubtopicKey(entry.topic.parentTopicSlug, 'direct', primarySubtopic.parentTopicSlug))"
              >
                <span class="tree-subtopic-label">{{ primarySubtopic.label }}</span>
                <span class="tree-subtopic-counts">{{ primarySubtopic.pageCount }} pages mentioning this cluster topic • {{ primarySubtopic.coreCount }} core • {{ primarySubtopic.mentionCount }} mentions</span>
              </button>

              <div *ngIf="isSubtopicExpanded(getSubtopicKey(entry.topic.parentTopicSlug, 'direct', primarySubtopic.parentTopicSlug))" class="tree-pages">
                <div class="tree-page-group">
                  <h5>Core Pages</h5>
                  <p class="tree-page-group-note">Pages where <strong>{{ primarySubtopic.label }}</strong> is a primary focus. Pages can belong to multiple clusters.</p>
                  <p class="tree-page-group-note">Topic signals: Top co-topics, weighted by how strongly the page is about each one.</p>
                  <div *ngFor="let page of getVisibleCorePages(primarySubtopic)" class="tree-page-row">
                    <a [href]="page.url" target="_blank" class="tree-page-url">{{ page.url }}</a>
                    <div class="tree-tag-row" *ngIf="page.topicTags && page.topicTags.length > 0">
                      <span class="tree-tag" *ngFor="let tag of page.topicTags">{{ getTreeTagLabel(tag) }} ({{ (tag.weight * 100).toFixed(0) }}%)</span>
                    </div>
                    <div class="tree-metric-row">
                      <span>Intent {{ formatTreeScore(page.seoIntentScore) }}</span>
                      <span>Specificity {{ formatTreeScore(page.specificityScore) }}</span>
                      <span>Weight {{ formatTreeScore(page.weight) }}</span>
                      <span>Salience {{ formatTreeScore(page.salience) }}</span>
                      <span>Density {{ formatTreeScore(page.relativeDensity) }}</span>
                    </div>
                    <div class="tree-metric-row tree-metric-row-secondary">
                      <span>Margin {{ formatTreeScore(page.marginToRunnerUp) }}</span>
                      <span>Ownership {{ formatTreeScore(page.ownershipScore) }}</span>
                      <span>Contrast {{ formatTreeScore(page.contrastScore) }}</span>
                      <span>Prevalence {{ formatTreeScore(page.topicPrevalence) }}</span>
                    </div>
                  </div>
                </div>

                <div class="tree-page-group">
                  <h5>Mention Pages</h5>
                  <p class="tree-page-group-note">Pages that reference the topic, but not strongly enough to qualify as core.</p>
                  <p class="tree-page-group-note">Topic signals: Top co-topics, weighted by how strongly the page is about each one.</p>
                  <div *ngFor="let page of getVisibleMentionPages(primarySubtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'direct', primarySubtopic.parentTopicSlug))" class="tree-page-row">
                    <a [href]="page.url" target="_blank" class="tree-page-url">{{ page.url }}</a>
                    <div class="tree-tag-row" *ngIf="page.topicTags && page.topicTags.length > 0">
                      <span class="tree-tag" *ngFor="let tag of page.topicTags">{{ getTreeTagLabel(tag) }} ({{ (tag.weight * 100).toFixed(0) }}%)</span>
                    </div>
                    <div class="tree-metric-row">
                      <span>Intent {{ formatTreeScore(page.seoIntentScore) }}</span>
                      <span>Specificity {{ formatTreeScore(page.specificityScore) }}</span>
                      <span>Weight {{ formatTreeScore(page.weight) }}</span>
                      <span>Salience {{ formatTreeScore(page.salience) }}</span>
                      <span>Density {{ formatTreeScore(page.relativeDensity) }}</span>
                    </div>
                    <div class="tree-metric-row tree-metric-row-secondary">
                      <span>Margin {{ formatTreeScore(page.marginToRunnerUp) }}</span>
                      <span>Ownership {{ formatTreeScore(page.ownershipScore) }}</span>
                      <span>Contrast {{ formatTreeScore(page.contrastScore) }}</span>
                      <span>Prevalence {{ formatTreeScore(page.topicPrevalence) }}</span>
                    </div>
                  </div>
                  <button
                    *ngIf="hasMoreMentionsVisible(primarySubtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'direct', primarySubtopic.parentTopicSlug))"
                    class="tree-more-btn"
                    (click)="showMoreMentions(getSubtopicKey(entry.topic.parentTopicSlug, 'direct', primarySubtopic.parentTopicSlug), primarySubtopic.mentionPages.length)"
                    type="button"
                  >
                    Show more mention pages ({{ getRemainingMentionCount(primarySubtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'direct', primarySubtopic.parentTopicSlug)) }})
                  </button>
                </div>
              </div>
            </div>
            <ng-template #noPrimaryClusterTopic>
              <div class="tree-empty">No primary cluster topic found.</div>
            </ng-template>
          </section>

          <section class="tree-subtopic-section">
            <h4>Directly Covered Subtopics</h4>
            <p class="tree-section-note">These subtopics are exclusive to the {{ entry.topic.label }} cluster.</p>
            <div *ngIf="getDirectVariantSubtopics(entry).length === 0" class="tree-empty">No additional directly covered subtopics.</div>
            <div *ngFor="let subtopic of getDirectVariantSubtopics(entry)" class="tree-subtopic-card">
              <button
                type="button"
                class="tree-subtopic-toggle"
                (click)="toggleSubtopic(getSubtopicKey(entry.topic.parentTopicSlug, 'direct', subtopic.parentTopicSlug))"
              >
                <span class="tree-subtopic-label">{{ subtopic.label }}</span>
                <span class="tree-subtopic-counts">{{ subtopic.pageCount }} pages mentioning this subtopic • {{ subtopic.coreCount }} core • {{ subtopic.mentionCount }} mentions</span>
              </button>

              <div *ngIf="isSubtopicExpanded(getSubtopicKey(entry.topic.parentTopicSlug, 'direct', subtopic.parentTopicSlug))" class="tree-pages">
                <div class="tree-page-group">
                  <h5>Core Pages</h5>
                  <p class="tree-page-group-note">Pages where <strong>{{ subtopic.label }}</strong> is a primary focus. Pages can belong to multiple clusters.</p>
                  <p class="tree-page-group-note">Topic signals: Top co-topics, weighted by how strongly the page is about each one.</p>
                  <div *ngFor="let page of getVisibleCorePages(subtopic)" class="tree-page-row">
                    <a [href]="page.url" target="_blank" class="tree-page-url">{{ page.url }}</a>
                    <div class="tree-tag-row" *ngIf="page.topicTags && page.topicTags.length > 0">
                      <span class="tree-tag" *ngFor="let tag of page.topicTags">{{ getTreeTagLabel(tag) }} ({{ (tag.weight * 100).toFixed(0) }}%)</span>
                    </div>
                    <div class="tree-metric-row">
                      <span>Intent {{ formatTreeScore(page.seoIntentScore) }}</span>
                      <span>Specificity {{ formatTreeScore(page.specificityScore) }}</span>
                      <span>Weight {{ formatTreeScore(page.weight) }}</span>
                      <span>Salience {{ formatTreeScore(page.salience) }}</span>
                      <span>Density {{ formatTreeScore(page.relativeDensity) }}</span>
                    </div>
                    <div class="tree-metric-row tree-metric-row-secondary">
                      <span>Margin {{ formatTreeScore(page.marginToRunnerUp) }}</span>
                      <span>Ownership {{ formatTreeScore(page.ownershipScore) }}</span>
                      <span>Contrast {{ formatTreeScore(page.contrastScore) }}</span>
                      <span>Prevalence {{ formatTreeScore(page.topicPrevalence) }}</span>
                    </div>
                  </div>
                </div>

                <div class="tree-page-group">
                  <h5>Mention Pages</h5>
                  <p class="tree-page-group-note">Pages that reference the topic, but not strongly enough to qualify as core.</p>
                  <p class="tree-page-group-note">Topic signals: Top co-topics, weighted by how strongly the page is about each one.</p>
                  <div *ngFor="let page of getVisibleMentionPages(subtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'direct', subtopic.parentTopicSlug))" class="tree-page-row">
                    <a [href]="page.url" target="_blank" class="tree-page-url">{{ page.url }}</a>
                    <div class="tree-tag-row" *ngIf="page.topicTags && page.topicTags.length > 0">
                      <span class="tree-tag" *ngFor="let tag of page.topicTags">{{ getTreeTagLabel(tag) }} ({{ (tag.weight * 100).toFixed(0) }}%)</span>
                    </div>
                    <div class="tree-metric-row">
                      <span>Intent {{ formatTreeScore(page.seoIntentScore) }}</span>
                      <span>Specificity {{ formatTreeScore(page.specificityScore) }}</span>
                      <span>Weight {{ formatTreeScore(page.weight) }}</span>
                      <span>Salience {{ formatTreeScore(page.salience) }}</span>
                      <span>Density {{ formatTreeScore(page.relativeDensity) }}</span>
                    </div>
                    <div class="tree-metric-row tree-metric-row-secondary">
                      <span>Margin {{ formatTreeScore(page.marginToRunnerUp) }}</span>
                      <span>Ownership {{ formatTreeScore(page.ownershipScore) }}</span>
                      <span>Contrast {{ formatTreeScore(page.contrastScore) }}</span>
                      <span>Prevalence {{ formatTreeScore(page.topicPrevalence) }}</span>
                    </div>
                  </div>
                  <button
                    *ngIf="hasMoreMentionsVisible(subtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'direct', subtopic.parentTopicSlug))"
                    class="tree-more-btn"
                    (click)="showMoreMentions(getSubtopicKey(entry.topic.parentTopicSlug, 'direct', subtopic.parentTopicSlug), subtopic.mentionPages.length)"
                    type="button"
                  >
                    Show more mention pages ({{ getRemainingMentionCount(subtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'direct', subtopic.parentTopicSlug)) }})
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section class="tree-subtopic-section">
            <h4>Related Topics</h4>
            <p class="tree-section-note">These topics are connected to this cluster but may also appear in other clusters.</p>
            <div *ngIf="getRelatedSubtopics(entry).length === 0" class="tree-empty">No related topics.</div>
            <div *ngFor="let subtopic of getRelatedSubtopics(entry)" class="tree-subtopic-card">
              <button
                type="button"
                class="tree-subtopic-toggle"
                (click)="toggleSubtopic(getSubtopicKey(entry.topic.parentTopicSlug, 'related', subtopic.parentTopicSlug))"
              >
                <span class="tree-subtopic-label">{{ subtopic.label }}</span>
                <span class="tree-subtopic-counts">{{ subtopic.pageCount }} pages mentioning this subtopic • {{ subtopic.coreCount }} core • {{ subtopic.mentionCount }} mentions</span>
              </button>

              <div *ngIf="isSubtopicExpanded(getSubtopicKey(entry.topic.parentTopicSlug, 'related', subtopic.parentTopicSlug))" class="tree-pages">
                <div class="tree-page-group">
                  <h5>Core Pages</h5>
                  <p class="tree-page-group-note">Pages where <strong>{{ subtopic.label }}</strong> is a primary focus. Pages can belong to multiple clusters.</p>
                  <p class="tree-page-group-note">Topic signals: Top co-topics, weighted by how strongly the page is about each one.</p>
                  <div *ngFor="let page of getVisibleCorePages(subtopic)" class="tree-page-row">
                    <a [href]="page.url" target="_blank" class="tree-page-url">{{ page.url }}</a>
                    <div class="tree-tag-row" *ngIf="page.topicTags && page.topicTags.length > 0">
                      <span class="tree-tag" *ngFor="let tag of page.topicTags">{{ getTreeTagLabel(tag) }} ({{ (tag.weight * 100).toFixed(0) }}%)</span>
                    </div>
                    <div class="tree-metric-row">
                      <span>Intent {{ formatTreeScore(page.seoIntentScore) }}</span>
                      <span>Specificity {{ formatTreeScore(page.specificityScore) }}</span>
                      <span>Weight {{ formatTreeScore(page.weight) }}</span>
                      <span>Salience {{ formatTreeScore(page.salience) }}</span>
                      <span>Density {{ formatTreeScore(page.relativeDensity) }}</span>
                    </div>
                    <div class="tree-metric-row tree-metric-row-secondary">
                      <span>Margin {{ formatTreeScore(page.marginToRunnerUp) }}</span>
                      <span>Ownership {{ formatTreeScore(page.ownershipScore) }}</span>
                      <span>Contrast {{ formatTreeScore(page.contrastScore) }}</span>
                      <span>Prevalence {{ formatTreeScore(page.topicPrevalence) }}</span>
                    </div>
                  </div>
                </div>

                <div class="tree-page-group">
                  <h5>Mention Pages</h5>
                  <p class="tree-page-group-note">Pages that reference the topic, but not strongly enough to qualify as core.</p>
                  <p class="tree-page-group-note">Topic signals: Top co-topics, weighted by how strongly the page is about each one.</p>
                  <div *ngFor="let page of getVisibleMentionPages(subtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'related', subtopic.parentTopicSlug))" class="tree-page-row">
                    <a [href]="page.url" target="_blank" class="tree-page-url">{{ page.url }}</a>
                    <div class="tree-tag-row" *ngIf="page.topicTags && page.topicTags.length > 0">
                      <span class="tree-tag" *ngFor="let tag of page.topicTags">{{ getTreeTagLabel(tag) }} ({{ (tag.weight * 100).toFixed(0) }}%)</span>
                    </div>
                    <div class="tree-metric-row">
                      <span>Intent {{ formatTreeScore(page.seoIntentScore) }}</span>
                      <span>Specificity {{ formatTreeScore(page.specificityScore) }}</span>
                      <span>Weight {{ formatTreeScore(page.weight) }}</span>
                      <span>Salience {{ formatTreeScore(page.salience) }}</span>
                      <span>Density {{ formatTreeScore(page.relativeDensity) }}</span>
                    </div>
                    <div class="tree-metric-row tree-metric-row-secondary">
                      <span>Margin {{ formatTreeScore(page.marginToRunnerUp) }}</span>
                      <span>Ownership {{ formatTreeScore(page.ownershipScore) }}</span>
                      <span>Contrast {{ formatTreeScore(page.contrastScore) }}</span>
                      <span>Prevalence {{ formatTreeScore(page.topicPrevalence) }}</span>
                    </div>
                  </div>
                  <button
                    *ngIf="hasMoreMentionsVisible(subtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'related', subtopic.parentTopicSlug))"
                    class="tree-more-btn"
                    (click)="showMoreMentions(getSubtopicKey(entry.topic.parentTopicSlug, 'related', subtopic.parentTopicSlug), subtopic.mentionPages.length)"
                    type="button"
                  >
                    Show more mention pages ({{ getRemainingMentionCount(subtopic, getSubtopicKey(entry.topic.parentTopicSlug, 'related', subtopic.parentTopicSlug)) }})
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </article>
    </div>
  </section>

  <!-- Topics Grid -->
  <section *ngIf="viewMode === 'list' && topics.length > 0" class="topics-section">
    <h2>Top Topics ({{ topics.length }} total)</h2>
    <div class="topics-grid">
      <div
        *ngFor="let topic of topics"
        class="topic-card"
        [class.selected]="selectedTopic === topic"
        (click)="selectTopic(topic)"
      >
        <div class="topic-header">
          <h3 class="topic-label">{{ topic.label }}</h3>
          <div class="topic-slug">{{ topic.parentTopicSlug }}</div>
          <div *ngIf="topic.topicTier" 
               [class]="getTierBadgeClass(topic.topicTier)"
               [title]="getTierDescription(topic.topicTier)">
            {{ getTierLabel(topic.topicTier) }}
          </div>
        </div>

        <div class="authority-score">
          <div class="score-value" [style.color]="getScoreColor(topic.authorityScore)">
            {{ topic.authorityScore.toFixed(1) }}
          </div>
          <div class="score-label">Authority Score</div>
        </div>

        <div class="metrics-grid">
          <div class="metric">
            <div class="metric-label" title="Number of pages covering the main topic">Breadth</div>
            <div class="metric-value">{{ (topic.breadthScore * 100).toFixed(1) }}%</div>
            <div class="metric-bar">
              <div
                class="metric-bar-fill"
                [style.width]="getScoreBarWidth(topic.breadthScore * 100)"
                [style.background-color]="getScoreColor(topic.breadthScore * 100)"
              ></div>
            </div>
          </div>

          <div class="metric">
            <div class="metric-label" title="Coverage contribution from related subtopics">Depth</div>
            <div class="metric-value">{{ (topic.depthScore * 100).toFixed(1) }}%</div>
            <div class="metric-bar">
              <div
                class="metric-bar-fill"
                [style.width]="getScoreBarWidth(topic.depthScore * 100)"
                [style.background-color]="getScoreColor(topic.depthScore * 100)"
              ></div>
            </div>
          </div>

          <div class="metric">
            <div class="metric-label" title="Combined coverage (breadth + depth)">Coverage</div>
            <div class="metric-value">{{ (topic.coverageScore * 100).toFixed(1) }}%</div>
            <div class="metric-bar">
              <div
                class="metric-bar-fill"
                [style.width]="getScoreBarWidth(topic.coverageScore * 100)"
                [style.background-color]="getScoreColor(topic.coverageScore * 100)"
              ></div>
            </div>
          </div>

          <div class="metric">
            <div class="metric-label">Strength</div>
            <div class="metric-value">{{ (topic.strengthScore * 100).toFixed(1) }}%</div>
            <div class="metric-bar">
              <div
                class="metric-bar-fill"
                [style.width]="getScoreBarWidth(topic.strengthScore * 100)"
                [style.background-color]="getScoreColor(topic.strengthScore * 100)"
              ></div>
            </div>
          </div>

          <div class="metric">
            <div class="metric-label">Focus</div>
            <div class="metric-value">{{ (topic.focusScore * 100).toFixed(1) }}%</div>
            <div class="metric-bar">
              <div
                class="metric-bar-fill"
                [style.width]="getScoreBarWidth(topic.focusScore * 100)"
                [style.background-color]="getScoreColor(topic.focusScore * 100)"
              ></div>
            </div>
          </div>

          <div class="metric" *ngIf="hasLinkScore(topic)">
            <div class="metric-label">Links</div>
            <div class="metric-value">{{ (topic.linkScore! * 100).toFixed(1) }}%</div>
            <div class="metric-bar">
              <div
                class="metric-bar-fill"
                [style.width]="getScoreBarWidth(topic.linkScore! * 100)"
                [style.background-color]="getScoreColor(topic.linkScore! * 100)"
              ></div>
            </div>
          </div>
        </div>

        <div class="topic-stats">
          <div class="stat">
            <span class="stat-label">Pages:</span>
            <span class="stat-value">
              {{ getDisplayPageCount(topic) }}
              <span 
                *ngIf="hasBoilerplatePenalty(topic)" 
                class="boilerplate-icon"
                [title]="getBoilerplateTooltip(topic)"
              >⚠️</span>
            </span>
          </div>
          <div class="stat">
            <span class="stat-label">Total Weight:</span>
            <span class="stat-value">{{ topic.totalWeight.toFixed(2) }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">Avg Weight:</span>
            <span class="stat-value">{{ topic.averageWeight.toFixed(2) }}</span>
          </div>
          <div class="stat" *ngIf="hasLinkScore(topic)">
            <span class="stat-label">Contextual Links:</span>
            <span class="stat-value">{{ topic.contextualLinkCount || 0 }}</span>
          </div>
        </div>

        <div *ngIf="topic.childTopicSlugs.length > 1" class="child-topics">
          <div class="child-topics-label">Merged topics ({{ topic.childTopicSlugs.length }}):</div>
          <div class="child-topics-list">
            <span *ngFor="let child of topic.childTopicSlugs.slice(0, 3)" class="child-tag">
              {{ child }}
            </span>
            <span *ngIf="topic.childTopicSlugs.length > 3" class="child-tag more">
              +{{ topic.childTopicSlugs.length - 3 }} more
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Old User List View (Dev only) -->
  <section *ngIf="!featureFlags.isUserView() && viewMode === 'old-user-list' && oldUserListTopics.length > 0" class="topics-section">
    <h2>Old User List View ({{ oldUserListTopics.length }} actionable)</h2>
    <div class="topics-grid">
      <div
        *ngFor="let topic of oldUserListTopics"
        class="topic-card user-view"
        [class.selected]="selectedTopic === topic"
        (click)="selectTopic(topic)"
      >
        <div class="topic-header">
          <h3 class="topic-label">{{ topic.label }}</h3>
          <div class="topic-slug">{{ topic.parentTopicSlug }}</div>
          <div *ngIf="topic.topicTier"
               [class]="getTierBadgeClass(topic.topicTier)"
               [title]="getTierDescription(topic.topicTier)">
            {{ getTierLabel(topic.topicTier) }}
          </div>
        </div>

        <div class="authority-score">
          <div class="score-value" [style.color]="getScoreColor(topic.authorityScore)">
            {{ topic.authorityScore.toFixed(1) }}
          </div>
          <div class="score-label">Authority Score</div>
        </div>

        <div class="action-buttons-container">
          <button
            class="action-btn diagnostic-btn"
            (click)="openDiagnostic(topic); $event.stopPropagation()"
          >
            View Diagnostic
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- Topic Details Panel -->
  <section *ngIf="selectedTopic" class="topic-details-panel">
    <div class="panel-header">
      <h2>Topic Details: {{ selectedTopic.label }}</h2>
      <button (click)="selectTopic(selectedTopic)" class="close-btn">×</button>
    </div>

    <div class="details-content">
      <div class="detail-section">
        <h3>Overview</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-label">Parent Topic Slug:</span>
            <span class="detail-value">{{ selectedTopic.parentTopicSlug }}</span>
          </div>
          <div class="detail-item" *ngIf="selectedTopic.topicTier">
            <span class="detail-label">Topic Tier:</span>
            <span class="detail-value">
              <span [class]="getTierBadgeClass(selectedTopic.topicTier)" [title]="getTierDescription(selectedTopic.topicTier)">
                {{ getTierLabel(selectedTopic.topicTier) }}
              </span>
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Authority Score:</span>
            <span class="detail-value" [style.color]="getScoreColor(selectedTopic.authorityScore)">
              {{ selectedTopic.authorityScore.toFixed(1) }}
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Pages Mentioning:</span>
            <div class="detail-value-wrapper">
              <span class="detail-value">
                {{ getDisplayPageCount(selectedTopic) }}
                <span 
                  *ngIf="hasBoilerplatePenalty(selectedTopic)" 
                  class="boilerplate-icon"
                  [title]="getBoilerplateTooltip(selectedTopic)"
                >⚠️</span>
              </span>
              <span *ngIf="hasBoilerplatePenalty(selectedTopic) && selectedTopic.effectivePageCountForCoverage" class="boilerplate-note">
                (effective: {{ selectedTopic.effectivePageCountForCoverage.toFixed(1) }}, actual: {{ selectedTopic.pageCount }})
              </span>
            </div>
          </div>
          <div class="detail-item">
            <span class="detail-label">Total Weight:</span>
            <span class="detail-value">{{ selectedTopic.totalWeight.toFixed(3) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Average Weight:</span>
            <span class="detail-value">{{ selectedTopic.averageWeight.toFixed(3) }}</span>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3>Score Breakdown</h3>
        <div class="score-breakdown">
          <div class="breakdown-item">
            <div class="breakdown-header">
              <span class="breakdown-label">Breadth Score</span>
              <span class="breakdown-value">{{ (selectedTopic.breadthScore * 100).toFixed(1) }}%</span>
            </div>
            <div class="breakdown-description">
              Number of pages covering the main topic
              <span *ngIf="hasBoilerplatePenalty(selectedTopic) && selectedTopic.effectivePageCountForCoverage">
                (effective: {{ selectedTopic.effectivePageCountForCoverage.toFixed(1) }} pages after boilerplate penalty, actual: {{ selectedTopic.pageCount }} pages)
              </span>
              <span *ngIf="!hasBoilerplatePenalty(selectedTopic)">
                ({{ selectedTopic.pageCount }} pages)
              </span>
            </div>
            <div class="breakdown-bar">
              <div
                class="breakdown-bar-fill"
                [style.width]="getScoreBarWidth(selectedTopic.breadthScore * 100)"
                [style.background-color]="getScoreColor(selectedTopic.breadthScore * 100)"
              ></div>
            </div>
          </div>

          <div class="breakdown-item">
            <div class="breakdown-header">
              <span class="breakdown-label">Depth Score</span>
              <span class="breakdown-value">{{ (selectedTopic.depthScore * 100).toFixed(1) }}%</span>
            </div>
            <div class="breakdown-description">
              Coverage contribution from related subtopics
              <span *ngIf="selectedTopic.relatedTopicsContribution && selectedTopic.relatedTopicsContribution > 0">
                (+{{ selectedTopic.relatedTopicsContribution.toFixed(1) }} pages from {{ selectedTopic.relatedTopicsBreakdown?.length || 0 }} related topics)
              </span>
              <span *ngIf="!selectedTopic.relatedTopicsContribution || selectedTopic.relatedTopicsContribution === 0">
                (no related topics contribution)
              </span>
            </div>
            <div class="breakdown-bar">
              <div
                class="breakdown-bar-fill"
                [style.width]="getScoreBarWidth(selectedTopic.depthScore * 100)"
                [style.background-color]="getScoreColor(selectedTopic.depthScore * 100)"
              ></div>
            </div>
          </div>

          <div class="breakdown-item">
            <div class="breakdown-header">
              <span class="breakdown-label">Coverage Score</span>
              <span class="breakdown-value">{{ (selectedTopic.coverageScore * 100).toFixed(1) }}%</span>
            </div>
            <div class="breakdown-description">
              Combined coverage (breadth + depth)
              <span *ngIf="selectedTopic.enhancedPageCountForCoverage">
                (enhanced: {{ selectedTopic.enhancedPageCountForCoverage.toFixed(1) }} pages = {{ selectedTopic.effectivePageCountForCoverage || selectedTopic.pageCount }} own + {{ selectedTopic.relatedTopicsContribution || 0 }} related)
              </span>
            </div>
            <div class="breakdown-bar">
              <div
                class="breakdown-bar-fill"
                [style.width]="getScoreBarWidth(selectedTopic.coverageScore * 100)"
                [style.background-color]="getScoreColor(selectedTopic.coverageScore * 100)"
              ></div>
            </div>
          </div>

          <div class="breakdown-item">
            <div class="breakdown-header">
              <span class="breakdown-label">Strength Score</span>
              <span class="breakdown-value">{{ (selectedTopic.strengthScore * 100).toFixed(1) }}%</span>
            </div>
            <div class="breakdown-description">
              Total cumulative investment (totalWeight: {{ selectedTopic.totalWeight.toFixed(2) }})
            </div>
            <div class="breakdown-bar">
              <div
                class="breakdown-bar-fill"
                [style.width]="getScoreBarWidth(selectedTopic.strengthScore * 100)"
                [style.background-color]="getScoreColor(selectedTopic.strengthScore * 100)"
              ></div>
            </div>
          </div>

          <div class="breakdown-item">
            <div class="breakdown-header">
              <span class="breakdown-label">Focus Score</span>
              <span class="breakdown-value">{{ (selectedTopic.focusScore * 100).toFixed(1) }}%</span>
            </div>
            <div class="breakdown-description">
              Average depth per page (avgWeight: {{ selectedTopic.averageWeight.toFixed(2) }})
            </div>
            <div class="breakdown-bar">
              <div
                class="breakdown-bar-fill"
                [style.width]="getScoreBarWidth(selectedTopic.focusScore * 100)"
                [style.background-color]="getScoreColor(selectedTopic.focusScore * 100)"
              ></div>
            </div>
          </div>

          <div class="breakdown-item" *ngIf="hasLinkScore(selectedTopic)">
            <div class="breakdown-header">
              <span class="breakdown-label">Link Score</span>
              <span class="breakdown-value">{{ (selectedTopic.linkScore! * 100).toFixed(1) }}%</span>
            </div>
            <div class="breakdown-description">
              {{ getLinkExplanation(selectedTopic) || `Contextual links in topic cluster (${selectedTopic.contextualLinkCount || 0} links)` }}
            </div>
            <div class="breakdown-bar">
              <div
                class="breakdown-bar-fill"
                [style.width]="getScoreBarWidth(selectedTopic.linkScore! * 100)"
                [style.background-color]="getScoreColor(selectedTopic.linkScore! * 100)"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="selectedTopic.relatedTopicsBreakdown && selectedTopic.relatedTopicsBreakdown.length > 0" class="detail-section">
        <h3>Related Topics Contribution</h3>
        <div class="related-topics-breakdown">
          <div class="breakdown-summary">
            <div class="summary-item">
              <span class="summary-label">Own Pages:</span>
              <span class="summary-value">{{ selectedTopic.effectivePageCountForCoverage || selectedTopic.pageCount }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Related Topics Contribution:</span>
              <span class="summary-value">+{{ selectedTopic.relatedTopicsContribution?.toFixed(1) || 0 }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Enhanced Coverage:</span>
              <span class="summary-value">{{ selectedTopic.enhancedPageCountForCoverage?.toFixed(1) || (selectedTopic.effectivePageCountForCoverage || selectedTopic.pageCount) }}</span>
            </div>
          </div>
          <div class="related-topics-list">
            <h4>Related Topics ({{ selectedTopic.relatedTopicsBreakdown.length }})</h4>
            <div *ngFor="let related of selectedTopic.relatedTopicsBreakdown" class="related-topic-item">
              <div class="related-topic-header">
                <span class="related-topic-slug">{{ related.topicSlug }}</span>
                <span class="related-topic-similarity">{{ (related.similarity * 100).toFixed(1) }}% similar</span>
              </div>
              <div class="related-topic-details">
                <span class="related-topic-pages">{{ related.pageCount }} pages</span>
                <span class="related-topic-contribution">+{{ related.contribution.toFixed(1) }} contribution</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="selectedTopic.topicTierReasoning && selectedTopic.topicTierReasoning.length > 0" class="detail-section">
        <h3>Classification Reasoning</h3>
        <div class="tier-reasoning">
          <p class="tier-description">{{ getTierDescription(selectedTopic.topicTier) }}</p>
          <ul class="reasoning-list">
            <li *ngFor="let reason of selectedTopic.topicTierReasoning" class="reasoning-item">
              {{ reason }}
            </li>
          </ul>
        </div>
      </div>

      <div *ngIf="selectedTopic.childTopicSlugs.length > 1" class="detail-section">
        <h3>Merged Child Topics ({{ selectedTopic.childTopicSlugs.length }})</h3>
        <div class="child-topics-grid">
          <div *ngFor="let child of selectedTopic.childTopicSlugs" class="child-topic-item">
            {{ child }}
          </div>
        </div>
      </div>

      <div *ngIf="hasLinkScore(selectedTopic)" class="detail-section">
        <div class="section-header">
          <h3>Link Details</h3>
          <button (click)="toggleLinks()" class="toggle-btn">
            {{ showLinks ? 'Hide' : 'Show' }} Links
          </button>
        </div>
        
        <div *ngIf="showLinks">
          <div *ngIf="loadingLinks" class="loading-message">
            Loading link details...
          </div>
          
          <div *ngIf="!loadingLinks">
            <div *ngIf="topicLinks.length > 0" class="links-section">
              <h4>Contextual Links ({{ topicLinks.length }})</h4>
              <p class="links-description">These links contribute to the link score and authority calculation.</p>
              <div class="links-list">
                <div *ngFor="let link of topicLinks" class="link-item">
                  <div class="link-source">
                    <strong>From:</strong> 
                    <a [href]="link.sourceUrl" target="_blank" class="link-url">{{ link.sourceUrl }}</a>
                  </div>
                  <div class="link-target">
                    <strong>To:</strong> 
                    <a [href]="link.targetUrl" target="_blank" class="link-url">{{ link.targetUrl }}</a>
                  </div>
                  <div class="link-details">
                    <span *ngIf="link.anchorText" class="anchor-text">Anchor: "{{ link.anchorText }}"</span>
                    <span *ngIf="!link.anchorText" class="anchor-text no-anchor">(no anchor text)</span>
                    <span class="confidence">Confidence: {{ (link.confidence * 100).toFixed(0) }}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div *ngIf="structuralLinks.length > 0" class="links-section">
              <h4>Structural Links ({{ structuralLinks.length }})</h4>
              <p class="links-description">These links are tracked but do not contribute to the link score.</p>
              <div class="links-list">
                <div *ngFor="let link of structuralLinks" class="link-item structural">
                  <div class="link-source">
                    <strong>From:</strong> 
                    <a [href]="link.sourceUrl" target="_blank" class="link-url">{{ link.sourceUrl }}</a>
                  </div>
                  <div class="link-target">
                    <strong>To:</strong> 
                    <a [href]="link.targetUrl" target="_blank" class="link-url">{{ link.targetUrl }}</a>
                  </div>
                  <div class="link-details">
                    <span *ngIf="link.anchorText" class="anchor-text">Anchor: "{{ link.anchorText }}"</span>
                    <span *ngIf="!link.anchorText" class="anchor-text no-anchor">(no anchor text)</span>
                    <span class="confidence">Confidence: {{ (link.confidence * 100).toFixed(0) }}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div *ngIf="topicLinks.length === 0 && structuralLinks.length === 0" class="no-links-message">
              No links found for this topic. Make sure links are classified and pages mention this topic.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  
  <!-- Side Panel for Topic Tree -->
  <div *ngIf="showSidePanel" class="side-panel-overlay" (click)="closeSidePanel()">
    <div class="side-panel" (click)="$event.stopPropagation()">
      <div class="side-panel-header">
        <h2 *ngIf="selectedTopicForTree">{{ selectedTopicForTree.label }}</h2>
        <button class="close-btn" (click)="closeSidePanel()">×</button>
      </div>
      
      <div class="side-panel-content">
        <div *ngIf="loadingTree" class="loading-message">Loading topic tree...</div>
        <div *ngIf="treeError" class="error-message">{{ treeError }}</div>
        
        <div *ngIf="topicTree && !loadingTree && !treeError" class="topic-tree-simple">
          <!-- Center Topic -->
          <div class="tree-center-simple">
            <div class="tree-node-center-simple">
              <h3>{{ topicTree.data.topic.label }}</h3>
              <div class="center-details">
                <span>Authority: {{ topicTree.data.topic.authorityScore.toFixed(1) }}</span>
                <span>Pages: {{ topicTree.data.topic.pageCount }}</span>
                <span *ngIf="topicTree.data.topic.grandparent" class="grandparent-info">
                  → Grandparent: <strong>{{ topicTree.data.topic.grandparent }}</strong>
                </span>
              </div>
            </div>
          </div>
          
          <!-- Path to Grandparent (Up) -->
          <div *ngIf="topicTree.data.upLevels.length > 0" class="tree-section">
            <h4 class="section-title">Path to Grandparent</h4>
            <div class="path-list">
              <div class="path-item path-start">
                <div class="path-node-label">{{ topicTree.data.topic.label }}</div>
                <div class="path-node-stats">
                  Authority: {{ topicTree.data.topic.authorityScore.toFixed(1) }} • Pages: {{ topicTree.data.topic.pageCount }}
                </div>
              </div>
              <div *ngFor="let level of topicTree.data.upLevels; let levelIndex = index" class="path-level">
                <div *ngFor="let node of level" class="path-item"
                     [style.border-left-color]="getRelationshipColor(node.relationshipType)">
                  <div class="path-arrow-up">↓</div>
                  <div class="path-node">
                    <div class="path-node-label">{{ node.label }}</div>
                    <div class="path-node-meta">
                      <span class="path-relationship" [style.color]="getRelationshipColor(node.relationshipType)">
                        {{ getRelationshipLabel(node.relationshipType) }}
                      </span>
                      <span *ngIf="node.similarity" class="path-similarity">
                        {{ (node.similarity * 100).toFixed(1) }}% similar to 
                        <strong *ngIf="levelIndex === 0">{{ topicTree.data.topic.label }}</strong>
                        <strong *ngIf="levelIndex > 0">{{ topicTree.data.upLevels[levelIndex - 1][0].label || 'previous step' }}</strong>
                      </span>
                    </div>
                    <div class="path-node-stats">
                      Authority: {{ node.authorityScore.toFixed(1) }} • Pages: {{ node.pageCount }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Related Topics (Down) -->
          <div *ngIf="topicTree.data.downLevels.length > 0" class="tree-section">
            <h4 class="section-title">Related Topics</h4>
            <div *ngFor="let level of topicTree.data.downLevels; let levelIndex = index" class="related-level">
              <div class="related-level-label">Level {{ levelIndex + 1 }}</div>
              <div class="related-nodes">
                <div *ngFor="let node of level" class="related-node"
                     [style.border-left-color]="getRelationshipColor(node.relationshipType)">
                  <div class="related-node-label">{{ node.label }}</div>
                  <div class="related-node-meta">
                    <span class="related-relationship" [style.color]="getRelationshipColor(node.relationshipType)">
                      {{ getRelationshipLabel(node.relationshipType) }}
                    </span>
                    <span *ngIf="node.similarity" class="related-similarity">
                      {{ (node.similarity * 100).toFixed(1) }}% similar to 
                      <strong *ngIf="levelIndex === 0">{{ topicTree.data.topic.label }}</strong>
                      <strong *ngIf="levelIndex > 0">previous level topics</strong>
                    </span>
                  </div>
                  <div class="related-node-stats">
                    Authority: {{ node.authorityScore.toFixed(1) }} • Pages: {{ node.pageCount }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Metrics -->
          <div *ngIf="topicTree.data.pathMetrics" class="tree-metrics-simple">
            <div class="metrics-title">Path Metrics</div>
            <div class="metrics-grid">
              <div class="metric-box">
                <div class="metric-value">{{ topicTree.data.pathMetrics.pathLength }}</div>
                <div class="metric-label">Steps</div>
              </div>
              <div *ngIf="topicTree.data.pathMetrics.averageSimilarity" class="metric-box">
                <div class="metric-value">{{ (topicTree.data.pathMetrics.averageSimilarity! * 100).toFixed(1) }}%</div>
                <div class="metric-label">Avg Similarity</div>
              </div>
              <div *ngIf="topicTree.data.pathMetrics.weakestLink" class="metric-box">
                <div class="metric-value" [style.color]="topicTree.data.pathMetrics.weakestLink! < 0.65 ? '#ef4444' : '#22c55e'">
                  {{ (topicTree.data.pathMetrics.weakestLink! * 100).toFixed(1) }}%
                </div>
                <div class="metric-label">Weakest Link</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Diagnostic View -->
  <app-topic-diagnostic
    *ngIf="showDiagnostic && diagnosticTopic"
    [domain]="domain"
    [topicSlug]="diagnosticTopic.parentTopicSlug"
    [onClose]="closeDiagnostic.bind(this)"
  ></app-topic-diagnostic>
</div>

